import queryString from "query-string";

/**
 * core/utils/Utils
 * ----------------------------------------------------------------------
 * Fornece funções utilitárias de uso geral.
 *
 * @since     0.0.1
 */

/**
 * Dicionário de acentuação, para substituição.
 *
 * @type {Object}
 */
const ACCENT = {
  source: "ÁÀÄÂÃÅÉÈÊËÍÌÎÏÓÒÔÕØÚÙÛÜÝÇÑáàäâãåéèêëíìîïóòôõøúùûüýÿçñ$&@°ºª¥£¢μ§",
  clean: "AAAAAAEEEEIIIIOOOOOUUUUYCNaaaaaaeeeeiiiiooooouuuuyycnseaooaylcus"
};

const HAS_OWN = {}.hasOwnProperty;

export const hasOwn = (obj, prop) => HAS_OWN.call(obj, prop);

/**
 * Faz o parsing de uma query string e retorna um objeto com os pares chave/valor.
 *
 * @param {*} query
 * @returns {Object}
 */
export const ParseQueryArgs = (query) => {
  return queryString.parse(query);
};

/**
 * Converte uma data em milisegundos para string de tempo e/ou data.
 *
 * @param {String|Number} date
 *     Data em milisegundos
 * @param {Boolean} withTime
 *     Se a strind retornada deve, ou não, conter o horário
 * @returns {String}
 */
export const DatetimeToLocaleDate = (date, withTime = false) => {
  if (typeof date !== "number") date = parseInt(date);

  if (isNaN(date)) return null;

  let currentDate = new Date(date);

  if (withTime === true) {
    return currentDate.toLocaleString("pt-BR");
  } else {
    return currentDate.toLocaleDateString("pt-BR");
  }
};

/**
 * Realiza soft-merge de objetos fornecidos como argumentos, um pouco menos
 * complexo que `Object.assign`.
 *
 * @param {...*} args
 *     Objetos para merge
 * @returns {Object}
 */
export const SoftMerge = function () {
  if (arguments.length > 0) {
    let mainArguments = {};

    for (let i = 0; i < arguments.length; i++) {
      let keys = Object.keys(arguments[i]);

      for (let k of keys) {
        mainArguments[k] = arguments[i][k];
      }
    }

    return mainArguments;
  }

  return {};
};

/**
 * Realiza merging de dois objetos de forma recursiva.
 *
 * @param {Object} target
 *     Objeto alvo
 * @param {Object} source
 *     Objeto com conteúdo p/ merge
 * @returns {*}
 */
export const MergeObjects = (target, source) => {
  for (let key of Object.keys(source)) {
    if (source[key] instanceof Object) {
      Object.assign(
        source[key],
        MergeObjects(target[key], source[key])
      );
    }
  }

  Object.assign(target || {}, source);

  return target;
};

/**
 * Retorna uma string URL-encoded do objeto de input.
 *
 * Port de uma reimplementação do jQuery disponível em:
 * https://github.com/knowledgecode/jquery-param/blob/master/jquery-param.js
 *
 * @param {Object|*} toSerialize
 *     Objeto a serializar
 * @returns {String}
 */
export const SerializeParams = (toSerialize) => {
  /**
   * Dados serializados.
   *
   * @type {Array}
   */
  let serialized = [];

  /**
   * Adiciona entradas no array de serialização.
   *
   * @param {String} key
   *     Chave associativa
   * @param {*} val
   *     Valor
   */
  let add = (key, val) => {
    val = (typeof val === "function") ? val() : val;
    val = (val === null || val === undefined) ? "" : val;
    serialized[serialized.length] = encodeURIComponent(key)
      + "="
      + encodeURIComponent(val);
  };

  /**
   * Serializa itens dentro do objeto em uma string codificada para URL.
   *
   * @param {String} prefix
   *     Prefixo a ser adicionado à entrada
   * @param {*} obj
   *     Objeto a serializar
   * @returns {Array}
   */
  let buildParam = (prefix, obj) => {
    let i,
      len,
      key;

    if (prefix) {
      if (Array.isArray(obj)) {
        for (i = 0, len = obj.length; i < len; i++) {
          buildParam(
            prefix
            + "["
            + ((typeof obj[i] === "object" && obj[i]) ? i : "")
            + "]",
            obj[i]
          );
        }
      } else if (String(obj) === "[object Object]") {
        for (key in obj) {
          buildParam(prefix + "[" + key + "]", obj[key]);
        }
      } else {
        add(prefix, obj);
      }
    } else if (Array.isArray(obj)) {
      for (i = 0, len = obj.length; i < len; i++) {
        if (
          obj[i].hasOwnProperty("name")
          && obj[i].hasOwnProperty("value")
        ) {
          add(obj[i].name, obj[i].value);
        } else {
          buildParam("[" + i + "]", obj[i]);
        }
      }
    } else {
      for (key in obj) {
        buildParam(key, obj[key]);
      }
    }

    return serialized;
  };

  return buildParam("", toSerialize).join("&");
};

/**
 * Sanitização simples de acentos.
 *
 * @param {String} value
 *     String a ser limpa
 * @returns {String}
 */
export const Sanitize = (value) => {
  for (let i = 0; i < ACCENT.source.length; i++) {
    value = value.replace(
      ACCENT.source[i],
      ACCENT.clean[i]
    );
  }

  return value.replace("ß", "ss").replace(/^-+|-+$/g, "");
};

/**
 * Sanitização completa de acentos.
 *
 * @param {String} value
 *     String a ser limpa
 * @param {Boolean} trim
 *     To trim or not, that's the question
 * @returns {String}
 */
export const SanitizeToSafeUrlName = (value, trim = false) => {
  trim = !(trim === false);

  if (typeof value !== "string" && value.toString) {
    value = value.toString();
  } else if (!value.toString) {
    return "";
  }

  if (trim === true) value = value.trim();

  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s\s/g, "-")
    .replace(/\s/g, "-");
};

/**
 * Adiciona zeros ou outro caractere desejado, à esquerda do número indicado.
 *
 * @param {Number} number
 *     Número a ser modificado
 * @param {Number} width
 *     Comprimento da string a ser retornada
 * @param {String} char
 *     Caractere para inserir à esquerda
 * @returns {String}
 */
export const PadWithZeroes = (number, width, char = '0') => {
  char = char || '0';
  number = number + '';
  return (number.length >= width)
    ? number
    : new Array(width - number.length + 1).join(char) + number;
};

/**
 * Remove caracteres que não sejam dígitos de uma string.
 *
 * @param {String|Number} value
 *     String para limpeza
 */
export const RemoveNonDigits = (value) => {
  if (typeof value === "number") value = value.toString();
  if (value === null || value === undefined || value === "") return "";
  return value.replace(/[^\d]/g, "");
};

/**
 * Formata `value` para o `locale` e `currency` desejados.
 *
 * @param {Number} value
 *     Valor a converter
 * @param {String} locale
 *     Código da localidade, padrão `pt-BR´
 * @param {String} currency
 *     Código de três letras representando a moeda, padrão `BRL`
 * @returns {String|Boolean}
 */
export const ToCurrency = (value, locale = "pt-BR", currency = "BRL") => {
  if (typeof value !== "number") return false;

  locale = locale || "pt-BR";
  currency = currency || "BRL";

  let numberFormat = new Intl.NumberFormat(
    locale,
    {
      style: "currency",
      currency: currency
    }
  );

  return numberFormat.format(value);
};

/**
 * Remove qualquer caractere que não seja numérico em uma string.
 * 
 * @param {String} value 
 *     Valor a ser sanitizado
 * @returns {String|Boolean}
 */
export const toNumber = (value) => {
  return value.replace(/[^\d]/g, "");
};

/**
 * Extrai o ID de vídeo do YouTube de uma URL, short URL, embed code ou
 * object embed code.
 *
 * @param {string} urlOrEmbed
 *     String contendo uma URL, short URL, embed ou object do YouTube
 * @returns {string|bool}
 */
export const ExtractYouTubeVideoId = (urlOrEmbed) => {
  let regexFragmentA = "(youtu.be|youtube.com)/",
    regexFragmentB = "(watch?(.*&?)?v=|(embed|v)/)?([^?&\"'>\r\n]+)",
    regex = new RegExp(regexFragmentA + regexFragmentB),
    matches;

  matches = regex.exec(urlOrEmbed.trim());
  if (matches !== null && matches !== "" && matches !== []) {
    return matches[matches.length - 1];
  }
  return false;
};

/**
 * Capitaliza uma palavra ou uma sentença de palavras em uma string.
 *
 * @param {string} value
 *     String para capitalização
 * @param {boolean} firstLetterOnly
 *     Se capitalizamos apenas a primeira letra
 * @returns {string}
 */
export const Capitalize = (value, firstLetterOnly = true) => {
  if (firstLetterOnly === true) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  let matches = value.match(/[\w]{1}[^\s\,]*/g),
      words = [];

  for (let n = 0; n < matches.length; n++) {
    let curr = matches[n];

    words.push(curr.charAt(0).toUpperCase() + curr.slice(1));
  }

  return words.join(" ");
};

/**
 * Copia texto em um bloco com `id` predefinido para a área de transferência.
 *
 * @param {string} id
 *     Identificador do campo
 * @param {function} callback
 *     Callback a ser chamado após o evento, opcional, recebe apenas um
 *     boolean indicando falha ou sucesso
 */
export const CopyToClipboard = (id, callback = null) => {
  if (!document && !navigator) {
    if (callback) callback(false);
    return;
  }

  let valueToCopy = document.getElementById(id).innerText
        || document.getElementById(id).value,
      isFirefox = typeof InstallTrigger !== 'undefined',
      isIE = /*@cc_on!@*/false || !!document.documentMode,
      isEdge = !isIE && !!window.StyleMedia,
      textArea;

  if (isFirefox || isIE || isEdge) {
    textArea = document.createElement("textarea");
    textArea.innerText = valueToCopy;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    let copied = document.execCommand("copy");
    textArea.remove();

    if (callback) callback(copied);
  } else {
    navigator
      .clipboard
      .writeText(valueToCopy)
      .then(() => {
        if (callback) callback(true);
      })
      .catch(err => {
        console.error("Não foi possível copiar.");
        if (callback) callback(false);
      });
  }
};
